import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { Weather } from "@/types/weather";
import dayjs from "dayjs";
import { Button, Chip, RangeValue, Skeleton, Spinner, DateValue } from "@heroui/react";
import {
    BarChart,
    Bar,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    ReferenceArea,
    Label,
} from "recharts";
import { roundTo } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { useMemo } from "react";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import useResizeObserver from "@/lib/utils/useResizeObserver";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { parseDate } from "@internationalized/date";

const CHART_COLORS = {
    primary: "#09426A",
    secondary: "#74BD43",
    teal: "#00C4EB",
} as const;

const CHART_HEIGHT = 250;

function WeatherCharts({ dateRange, setDateRange }: { dateRange: RangeValue<DateValue> | null, setDateRange: (dateRange: RangeValue<DateValue> | null) => void }) {
    const { t } = useTranslation("insights");
    const { formatDateMMDDYYYY } = useI18n();
    const { useGetRequest } = useRequest();
    const { formatUnit } = useI18n();

    const { fieldId, selectedCropCycle, fieldIsComplete, fieldIsAdvanced } =
        useFieldDashboard() ?? {};

    const lastDayOfGraph = useMemo(() => {
        return dayjs(dateRange?.end?.toString());
    }, [dateRange?.end]);

    const weatherQuery = useGetRequest<Weather[]>({
        url: `/fields/${fieldId}/weather/`,
        enabled: !!fieldId && !!selectedCropCycle,
        params: {
            start_date: dayjs
                .min(dayjs(selectedCropCycle?.start_date), dayjs(dateRange?.start?.toString() ?? ""))
                .format("YYYY-MM-DD"),
            end_date: lastDayOfGraph.format("YYYY-MM-DD"),
        },
    });

    const [startDate, endDate] = useMemo(() => {
        return [dayjs(dateRange?.start?.toString()), dayjs(dateRange?.end?.toString())];
    }, [dateRange]);

    const weatherData = useMemo(() => {
        return sortArrayByKey(
            weatherQuery.data?.filter((d) => {
                const date = dayjs(d.date);
                return (
                    date.isSameOrAfter(startDate, "day") &&
                    date.isSameOrBefore(dayjs(endDate))
                );
            }) ?? [],
            "date",
        ).map((row) => ({
            ...row,
            date: dayjs(row.date).toDate(),
        }));
    }, [endDate, startDate, weatherQuery.data]);

    const { elementRef, isResizing } = useResizeObserver(250);

    const forecastReferenceArea = useMemo(() => {
        const maxWeatherValue = dayjs.max(
            weatherData.map((d) => dayjs(d.date)),
        );
        if (!maxWeatherValue) return null;

        return (
            <>
                <ReferenceArea
                    x1={getTodayWithTimeTravel()
                        .endOf("day")
                        .toDate()
                        .getTime()}
                    x2={dayjs
                        .min(
                            maxWeatherValue,
                            getTodayWithTimeTravel().add(14, "day"),
                        )
                        .toDate()
                        .getTime()}
                    stroke={CHART_COLORS.primary}
                    strokeOpacity={0.5}
                    fill={CHART_COLORS.primary}
                    fillOpacity={0.05}
                >
                    <Label
                        value="Forecasted"
                        position="top"
                        fontSize={10}
                        dx={-14}
                    />
                </ReferenceArea>
                <ReferenceArea
                    x1={getTodayWithTimeTravel()
                        .add(14, "day")
                        .endOf("day")
                        .toDate()
                        .getTime()}
                    x2={dayjs
                        .min(
                            maxWeatherValue,
                            getTodayWithTimeTravel().add(35, "day"),
                        )
                        .toDate()
                        .getTime()}
                    stroke={CHART_COLORS.teal}
                    strokeOpacity={0.5}
                    fill={CHART_COLORS.teal}
                    fillOpacity={0.05}
                >
                    <Label value="Extended" position="top" fontSize={10} />
                </ReferenceArea>
                <ReferenceArea
                    x1={getTodayWithTimeTravel()
                        .add(35, "day")
                        .endOf("day")
                        .toDate()
                        .getTime()}
                    x2={dayjs
                        .min(
                            maxWeatherValue,
                            getTodayWithTimeTravel().add(215, "day"),
                        )
                        .toDate()
                        .getTime()}
                    stroke={CHART_COLORS.secondary}
                    strokeOpacity={0.5}
                    fill={CHART_COLORS.secondary}
                    fillOpacity={0.05}
                >
                    <Label value="Seasonal" position="top" fontSize={10} />
                </ReferenceArea>
            </>
        );
    }, [weatherData]);

    const showingForecast = useMemo(() => {
        return (
            dayjs(endDate).isSameOrBefore(dayjs().add(14, "day")) &&
            dayjs(endDate).isAfter(dayjs())
        );
    }, [endDate]);

    const showingExtendedForecast = useMemo(() => {
        return (
            dayjs(endDate).isAfter(dayjs().add(14, "day")) &&
            dayjs(endDate).isSameOrBefore(dayjs().add(35, "day"))
        );
    }, [endDate]);
    const showingSeasonalForecast = useMemo(() => {
        return dayjs(endDate).isAfter(dayjs().add(35, "day"));
    }, [endDate]);

    const precipSum = useMemo(() => {
        return roundTo(
            weatherData.reduce((acc, curr) => acc + curr.precipitation, 0),
            2,
        );
    }, [weatherData]);

    return (
        <div className="w-full h-[38rem]" ref={elementRef}>
            {isResizing ? (
                <Skeleton className="h-full w-full" />
            ) : weatherQuery.isLoading ? (
                <div className="flex justify-center items-center h-full">
                    <Spinner label={t("messages.loadingWeatherData")} />
                </div>
            ) : !weatherData.length ? (
                <div className="text-sm italic text-center mt-4">
                    {t("messages.noDataSelectedDates")}
                </div>
            ) : (
                <div className="flex flex-col gap-8">
                    <div className="flex flex-row gap-2 justify-end">
                        <Button
                            color={showingForecast ? "primary" : "default"}
                            onPress={() => setDateRange({
                                start: parseDate(startDate.format("YYYY-MM-DD")),
                                end: parseDate(getTodayWithTimeTravel()
                                    .add(14, "day")
                                    .format("YYYY-MM-DD")),
                            })}
                            size="sm"
                            startContent={
                                showingForecast && (
                                    <FontAwesomeIcon icon={faCheck} />
                                )
                            }
                        >
                            Forecast
                        </Button>
                        {(fieldIsAdvanced || fieldIsComplete) && (
                            <Button
                                color={
                                    showingExtendedForecast
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() =>
                                    setDateRange({
                                        start: parseDate(startDate.format("YYYY-MM-DD")),
                                        end: parseDate(getTodayWithTimeTravel()
                                            .add(35, "day")
                                            .format("YYYY-MM-DD")),
                                    })}

                                size="sm"
                                startContent={
                                    showingExtendedForecast && (
                                        <FontAwesomeIcon icon={faCheck} />
                                    )
                                }
                            >
                                Extended
                            </Button>
                        )}

                        {fieldIsComplete && (
                            <Button
                                color={
                                    showingSeasonalForecast
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() =>
                                    setDateRange({
                                        start: parseDate(startDate.format("YYYY-MM-DD")),
                                        end: parseDate(getTodayWithTimeTravel()
                                            .add(215, "day")
                                            .format("YYYY-MM-DD")),
                                    })}
                                size="sm"
                                startContent={
                                    showingSeasonalForecast && (
                                        <FontAwesomeIcon icon={faCheck} />
                                    )
                                }
                            >
                                Seasonal
                            </Button>
                        )}
                    </div>
                    <div className="font-semibold">
                        {t("temperature")}
                    </div>
                    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                        <LineChart
                            data={weatherData}
                            margin={{ bottom: 8, left: 8, top: 15 }}
                            syncId="weather"
                        >
                            <XAxis
                                dataKey="date"
                                scale="time"
                                spacing={50}
                                angle={-8}
                                fontSize={12}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                            />
                            <YAxis
                                label={{
                                    value: `${t("temperature")} ${formatUnit("F", "C")}`,
                                    angle: -90,
                                    dx: -30,
                                }}
                                fontSize={12}
                            />
                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value, name) => [
                                    `${roundTo(value as number, 1)} ${formatUnit("F", "C")}`,
                                    name === "max_temperature"
                                        ? t("chartLabels.max")
                                        : t("chartLabels.min"),
                                ]}
                            />
                            <Line
                                dataKey="max_temperature"
                                stroke={CHART_COLORS.secondary}
                                dot={false}
                                strokeWidth={2}
                            />
                            <Line
                                dataKey="min_temperature"
                                stroke={CHART_COLORS.teal}
                                dot={false}
                                strokeWidth={2}
                            />
                            {forecastReferenceArea}
                        </LineChart>
                    </ResponsiveContainer>

                    <div className="flex flex-row justify-between">
                        <div className="font-semibold">
                            {t("chartLabels.precipitation")}
                        </div>
                        <Chip
                            color="primary"
                            size="sm"
                            radius="sm"
                            variant="flat"
                            classNames={{
                                content: "text-[0.65rem] text-center",
                            }}
                        >
                            {precipSum} {formatUnit("in", "mm")} Total
                            Precipitation
                        </Chip>
                    </div>
                    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                        <BarChart
                            data={weatherData}
                            margin={{ bottom: 8, left: 8, top: 15 }}
                            syncId="weather"
                        >
                            <XAxis
                                dataKey="date"
                                scale="time"
                                spacing={50}
                                angle={-8}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                fontSize={12}
                            />
                            <YAxis
                                label={{
                                    value: `${t("chartLabels.precipitation")} (${formatUnit("in", "mm")})`,
                                    angle: -90,
                                    dx: -30,
                                }}
                                fontSize={12}
                            />
                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value) => [
                                    `${roundTo(value as number, 1)}`,
                                    `${t("chartLabels.precipitation")} (${formatUnit("in", "mm")})`,
                                ]}
                            />

                            <Bar
                                dataKey="precipitation"
                                stroke={CHART_COLORS.teal}
                                fill={CHART_COLORS.primary}
                                barSize={10}
                            />
                            {forecastReferenceArea}
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="font-semibold">
                        {t("chartLabels.humidity")}
                    </div>
                    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                        <LineChart
                            data={weatherData}
                            margin={{ bottom: 16, left: 8, top: 15 }}
                            syncId="weather"
                        >
                            <XAxis
                                dataKey="date"
                                scale="time"
                                spacing={50}
                                angle={-8}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                fontSize={12}
                            />
                            <YAxis
                                label={{
                                    value: `${t("chartLabels.humidity")} %`,
                                    angle: -90,
                                    dx: -30,
                                }}
                                fontSize={12}
                            />
                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value) => [
                                    `${roundTo(value as number, 1)}`,
                                    `${t("chartLabels.humidity")} %`,
                                ]}
                            />

                            <Line
                                dataKey="relative_humidity"
                                stroke={CHART_COLORS.teal}
                                fill={CHART_COLORS.teal}
                                dot={false}
                                strokeWidth={2}
                            />

                            {forecastReferenceArea}
                        </LineChart>
                    </ResponsiveContainer>
                    <div className="font-semibold">
                        {t("windSpeed")}
                    </div>
                    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                        <LineChart
                            data={weatherData}
                            margin={{ bottom: 16, left: 8, top: 15 }}
                            syncId="weather"
                        >
                            <XAxis
                                dataKey="date"
                                scale="time"
                                spacing={50}
                                angle={-8}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                fontSize={12}
                            />
                            <YAxis
                                label={{
                                    value: `${t("windSpeed")} (${formatUnit("mph", "km_h")})`,
                                    angle: -90,
                                    dx: -30,
                                }}
                                fontSize={12}
                            />
                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value) => [
                                    `${roundTo(value as number, 1)}`,
                                    `${t("windSpeed")} (${formatUnit("mph", "km_h")})`,
                                ]}
                            />

                            <Line
                                dataKey="wind_speed"
                                stroke={CHART_COLORS.primary}
                                fill={CHART_COLORS.secondary}
                                dot={false}
                                strokeWidth={2}
                            />

                            {forecastReferenceArea}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

export default WeatherCharts;
